import React, { Fragment, useCallback, useContext, useEffect, useState } from "react";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { EkasiServeAppBar, equalObj, formatPercentage, isNumber, LoadingPage, parseNumber, selectAllOnFocus, SetState } from "./Common";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import { Link, useNavigate, useParams } from "react-router-dom";
import TextField from "@mui/material/TextField";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Snackbar from "@mui/material/Snackbar";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import { Client } from "./Clients";
import { Container, InputAdornment, List, ListItem, ListItemButton, Popover } from "@mui/material";
import { UserContext, UserRoleAdmin } from "./Users";
import { Add, Edit } from "@mui/icons-material";
import { ClientDetails } from "./ClientDetails";

export interface Company {
    id: number;
	name: string;
	addr0: string;
	addr1: string;
	addr2: string;
	vat_registration: string;
    vat_rate: number;
    vat_rate_text: string
    service_fee: number;
    service_fee_text: string;
	service_fee_on_vat_total: number;
	service_fee_includes_vat: number;
	round_values: number;
	show_vat_breakdown: number;
}

function ClientsButton({ company }: { company: Company }) {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    return (
        <Fragment>
            <Button aria-describedby="Clients" onClick={(ev) => setAnchorEl(ev.currentTarget)} variant="contained" fullWidth>
                Clients
            </Button>
            <Popover
                id="Clients"
                open={anchorEl !== null}
                onClose={() => setAnchorEl(null)}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>

                <List>
                    <ListItem>
                        <ListItemButton component={Link} to={"/companies/" + company.id + "/clients"}> Show Clients </ListItemButton>
                    </ListItem>
                    <ListItem>
                        <ListItemButton onClick={(ev) => navigate("/clients/0", { state: {companyId: company.id || 0} })}> Create Client </ListItemButton>
                    </ListItem>
                </List>
            </Popover>
        </Fragment>
    )
}

function QuotesButton({ company }: { company: Company }) {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    return (
        <Fragment>
            <Button aria-describedby="Quotes" onClick={(ev) => setAnchorEl(ev.currentTarget)} variant="contained" fullWidth>
                Quotes
            </Button>
            <Popover
                id="Quotes"
                open={anchorEl !== null}
                onClose={() => setAnchorEl(null)}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>

                <List>
                    <ListItem>
                        <ListItemButton component={Link} to={"/companies/" + company.id + "/quotes"}> Show Quotes </ListItemButton>
                    </ListItem>
                    <ListItem>
                        <ListItemButton onClick={(ev) => navigate("/quotes/0", { state: {companyIdText: company.id || 0} })}> Create Quote </ListItemButton>
                    </ListItem>
                </List>
            </Popover>
        </Fragment>
    )
}

function CompaniesTable( { companies } : {companies: Array<Company>}) {
    const auth = useContext(UserContext);

    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={12}>
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="20%">Name</TableCell>
                                    <TableCell width="30%">Address</TableCell>
                                    <TableCell width="15%">VAT Registration</TableCell>
                                    <TableCell width="15%"> 
                                    </TableCell>
                                    <TableCell width="15%">
                                    </TableCell>
                                    {auth.user.role === UserRoleAdmin && <TableCell width="5%"> 
                                        <Button variant="contained" component={Link} to="/companies/0" fullWidth>
                                            <Add />
                                        </Button>
                                    </TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {companies.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{row.name}</TableCell>
                                        <TableCell>{row.addr0}<br />{row.addr1}<br />{row.addr2}</TableCell>
                                        <TableCell>{row.vat_registration}</TableCell>
                                        <TableCell>
                                            <QuotesButton company={row} />
                                        </TableCell>
                                        <TableCell>
                                            <ClientsButton company={row} />
                                        </TableCell>
                                        {auth.user.role === UserRoleAdmin && <TableCell>
                                            <Button variant="contained" component={Link} to={"/companies/" + row.id} fullWidth>
                                                <Edit />
                                            </Button>
                                        </TableCell>}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

            </Grid>
        </Grid>
    )
}

export const CompaniesPage = () => {
    const [companies, setCompanies] = useState<Array<Company>>([]);
    const [snackbar, setSnackbar] = useState("");

    useEffect(() => {
        const fetchCompanies = async () => {
            const response = await fetch("/api/v1/companies");
            const data = await response.json();
            setCompanies(data.result || []);
            if (data.error) {
                setSnackbar(data.error);
            }
        };
        fetchCompanies();
    }, []);

    return (
        <Box>
            <EkasiServeAppBar />
            <Grid component={Container} container spacing={2} sx={{ alignItems: "center" }}>
                <Grid size={12}> <h1> Companies </h1> </Grid>
                <Grid size={12}>
                    <CompaniesTable companies={companies} />
                </Grid>
            </Grid>
            <Snackbar
                open={snackbar !== ""}
                autoHideDuration={6000}
                onClose={(ev, reason) => setSnackbar("")}
                message={snackbar}/>
        </Box>
    )
}

function CompanyForm({ company, setCompany } : { company: Partial<Company>, setCompany: SetState<Partial<Company>> }) {
    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={{ xs: 6, md: 9 }}>
                <TextField
                    id="name"
                    label="Name"
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setCompany(company => ({ ...company, name: e.target.value }))}
                    fullWidth
                    value={company.name || ""}
                />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
                <TextField
                    id="vat"
                    label="VAT Reg."
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setCompany(company => ({ ...company, vat_registration: e.target.value }))}
                    fullWidth
                    value={company.vat_registration || ""} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                    id="vat_rate"
                    label="VAT Rate"
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setCompany(company => ({ ...company, vat_rate: parseNumber(e.target.value) / 100, vat_rate_text: e.target.value }))}
                    error={!isNumber(company.vat_rate_text || "")}
                    fullWidth
                    value={company.vat_rate_text || ""} 
                    slotProps={{
                        input: {
                            endAdornment: <InputAdornment position="end">%</InputAdornment>
                        }
                    }}/>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                    id="service_fee"
                    label="Service Fee"
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setCompany(company => ({ ...company, service_fee: parseNumber(e.target.value) / 100, service_fee_text: e.target.value }))}
                    error={!isNumber(company.service_fee_text || "")}
                    fullWidth
                    value={company.service_fee_text || ""} 
                    slotProps={{
                        input: {
                            endAdornment: <InputAdornment position="end">%</InputAdornment>
                        }
                    }} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                    id="addr0"
                    label="Street"
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setCompany(company => ({ ...company, addr0: e.target.value }))}
                    fullWidth
                    value={company.addr0 || ""} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                    id="addr1"
                    label="Building"
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setCompany(company => ({ ...company, addr1: e.target.value }))}
                    fullWidth
                    value={company.addr1 || ""} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                    id="addr2"
                    label="City, Post Code"
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setCompany(company => ({ ...company, addr2: e.target.value }))}
                    fullWidth
                    value={company.addr2 || ""} />
            </Grid>

            <Grid size={{ xs: 6, md: 3 }}>
                <FormGroup>
                    <FormControlLabel
                        checked={company.service_fee_on_vat_total === 1}
                        control={<Checkbox />}
                        onChange={(e, checked) => setCompany(company => ({ ...company, service_fee_on_vat_total: checked ? 1 : 0 }))}
                        label="Service Fee On VAT Total" />
                </FormGroup>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
                <FormGroup>
                    <FormControlLabel
                        checked={company.service_fee_includes_vat === 1}
                        control={<Checkbox />}
                        onChange={(e, checked) => setCompany(company => ({ ...company, service_fee_includes_vat: checked ? 1 : 0 }))}
                        label="Service Fee Includes VAT" />
                </FormGroup>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
                <FormGroup>
                    <FormControlLabel
                        checked={company.round_values === 1}
                        control={<Checkbox />}
                        onChange={(e, checked) => setCompany(company => ({ ...company, round_values: checked ? 1 : 0 }))}
                        label="Round Values" />
                </FormGroup>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
                <FormGroup>
                    <FormControlLabel
                        checked={company.show_vat_breakdown === 1}
                        control={<Checkbox />}
                        onChange={(e, checked) => setCompany(company => ({ ...company, show_vat_breakdown: checked ? 1 : 0 }))}
                        label="Show VAT Breakdown" />
                </FormGroup>
            </Grid>
        </Grid>
    )
}

export const CompanyPage = () => {
    const { companyIdText } = useParams();
    const [company, setCompany] = useState<Partial<Company>>({});
    const [snackbar, setSnackbar] = useState("");
    const [requestDelete, setRequestDelete] = useState(false);
    const [savedCompany, setSavedCompany] = useState<Partial<Company>>({});
    const [edited, setEdited] = useState(false);
    const [isLoading, setIsLoading] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchCompany = async () => {
            const response = await fetch("/api/v1/companies/" + (companyIdText || "0"));
            const data = await response.json();
            data.result.service_fee_text = formatPercentage(data?.result?.service_fee);
            data.result.vat_rate_text = formatPercentage(data?.result?.vat_rate);
            setCompany(data.result || {});
            setSavedCompany(data.result || {});
            if (data.error) {
                setSnackbar(data.error);
            }
            setIsLoading(lock => lock - 1);
        }
        setIsLoading(lock => lock + 1);
        fetchCompany();
    }, [companyIdText]);

    useEffect(() => {
        setEdited(!equalObj(company, savedCompany));
    }, [company, savedCompany]);

    const saveCompany = useCallback(async () => {
        const response = await fetch("/api/v1/companies", {
            method: (company.id || 0) === 0 ? "POST" : "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(company)
        });
        const data = await response.json();
        data.result.service_fee_text = formatPercentage(data?.result?.service_fee);
        data.result.vat_rate_text = formatPercentage(data?.result?.vat_rate);
        setCompany(data.result || {});
        setSavedCompany(data.result || {});
        if (data.error) {
            setSnackbar(data.error);
        } else {
            if (companyIdText === "0") {
                navigate("/companies/" + data.result.id, { replace: true });
            }
            setSnackbar("Information Saved");
        }
    }, [company, companyIdText, navigate]);

    const deleteCompany = useCallback(async () => {
        const response = await fetch("/api/v1/companies/" + company?.id, { method: "DELETE" });
        const data = await response.json();
        if (data.error) {
            setSnackbar(data.error);
        } else {
            setCompany(data.result || {});
            navigate(-1);
        }
    }, [company?.id, navigate]);

    if (isLoading) return (<LoadingPage />)

    return (
        <Stack spacing={0}>
            <EkasiServeAppBar />
            <Grid component={Container} container spacing={2} sx={{ alignItems: "center" }}>
                <Grid size={{ xs: 12, md: 8 }}>
                    <h1>{company?.name || "New Company"}</h1>
                </Grid>
                <Grid size={{ xs: 6, md: 2}}>
                    {
                        ((company.id || 0) === 0)
                            ? <Button variant="contained" onClick={() => navigate(-1)} fullWidth> Cancel </Button>
                            : <Button variant="contained" onClick={() => setRequestDelete(true)} color="error" fullWidth> Delete </Button>
                    }
                </Grid>
                <Grid size={{ xs: 6, md: 2}}>
                    <Button disabled={!edited} variant="contained" onClick={saveCompany} fullWidth> Save </Button>
                </Grid>
                <Grid size={12}>
                    <CompanyForm company={company} setCompany={setCompany} />
                </Grid>
            </Grid>

            <Snackbar
                open={snackbar !== ""}
                autoHideDuration={6000}
                onClose={(ev, reason) => setSnackbar("")}
                message={snackbar}/>
            <Dialog open={requestDelete}>
                <DialogTitle> Are you sure you want to delete this company? </DialogTitle>
                <Button onClick={deleteCompany}> Yes, Delete </Button>
                <Button onClick={() => setRequestDelete(false)}> No, Cancel </Button>
            </Dialog>
        </Stack>

    )
}

export function CompaniesSelect({ companies, client, setClient, setClientDetail } : { companies: Array<Company>, client: Partial<Client>, setClient: SetState<Partial<Client>>, setClientDetail: SetState<Partial<ClientDetails>> }) {
    return (
        <FormControl fullWidth>
            <InputLabel>Company</InputLabel>
            <Select
                label="Company"
                value={companies.length === 0 ? "" : (client.company_id || "")}
                onChange={(ev) => { setClientDetail(detail => ({ ...detail, client_id: undefined })); setClient(client => ({ ...client, company_id: ev.target.value as number })) }}
            >
                {companies.map((company) => (
                    <MenuItem value={company.id}>{company.name}</MenuItem>
                ))}
            </Select>
        </FormControl>
    )
}